import ArrowForward from "@mui/icons-material/ArrowForward";
import { LoadingButton } from "@mui/lab";
import { Box, IconButton, Switch, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { BalootUserAPIs } from "../../../api/APIs";
import { useRequest } from "../../../api/utils/useRequest";
import EditSubscriptionBox from "../../../components/direct-debit/EditSubscriptionBox";
import { ActionModal } from "../../../components/shared/ActionModal";
import AppBarContainer from "../../../components/shared/AppBarContainer";
import BottomFullWidthButtonContainer from "../../../components/shared/BottomFullWidthButtonContainer";
import CustomRectangularSkeleton from "../../../components/shared/CustomRectangularSkeleton";
import CustomTextSkeleton from "../../../components/shared/CustomTextSkeleton";
import InfoBox from "../../../components/shared/InfoBox";
import PageContentContainer from "../../../components/shared/PageContentContainer";
import { useSnackBar } from "../../../context/SnackBarContext";
import { sendEnableRecurring } from "../../../metrix/metrixWrapper";
import { DirectDebitStatusType } from "../../../types/DirectDebitTypes";
import { toToman } from "../../../utils/Utils";
import { MobileNoBottomSheet } from "../../../components/direct-debit/MobileNoBottomSheet";

function DirectDebitMainPage() {
  const openSnack = useSnackBar();
  const {
    getLoading,
    enableChecked,
    setEnableChecked,
    editableSubscription,
    serverSubscription,
    getSubscriptionStatus,
  } = useOutletContext();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [mobileBoxVisibility, setMobileBoxVisibility] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const [mobileNo, setMobileNo] = useState(null);
  const [open, setOpen] = useState(false);

  const { loading: preLoading, apiCall: setPreSubscription } = useRequest(
    BalootUserAPIs.setPreSubscription
  );

  const { loading: deleteLoading, apiCall: deleteSubscription } = useRequest(
    BalootUserAPIs.deleteSubscription
  );

  const { loading, apiCall: setSubscription } = useRequest(
    BalootUserAPIs.setSubscription
  );

  const onDelete = () => {
    deleteSubscription({
      data: { id: serverSubscription.id },
      onSuccess: () => {
        setOpenModal(false);
        getSubscriptionStatus();
        openSnack(true, {
          message: "پس‌انداز خودکار شما غیرفعال شد.",
        });
      },
    });
  };

  const isButtonDisable = () => {
    if (serverSubscription.status === DirectDebitStatusType.ACTIVE) {
      return (
        serverSubscription.amount === editableSubscription.amount &&
        serverSubscription.bank.id === editableSubscription.bank.id &&
        serverSubscription.day === editableSubscription.day &&
        serverSubscription.period === editableSubscription.period
      );
    } else {
      return !editableSubscription.bank;
    }
  };

  const save = () => {
    if (
      editableSubscription.amount > editableSubscription.bank.directDebitLimit
    ) {
      setErrorMessage(
        `مقدار وارد شده از محدودیت بانک انتخابی شما (${toToman(
          editableSubscription.bank.directDebitLimit
        )} تومان) بیشتر است.`
      );
    } else {
      let data = {
        bankId: editableSubscription.bank.id,
        day: editableSubscription.day,
        limit: editableSubscription.amount,
        period: editableSubscription.period,
      };
      setPreSubscription({
        data: data,
        onSuccess: (response) => {
          if (response.newContract) {
            let mobile = response.mobile
              ? "0" + response.mobile.replace(/[^a-zA-Z0-9+]/g, "").substr(3)
              : "";
            setMobileNo(mobile);
            setMobileBoxVisibility(true);
            setOpen(true);
          } else {
            submitSubscription(response);
          }
        },
      });
    }
  };

  const submitSubscription = (response) => {
    setSubscription({
      data: {
        bankId: editableSubscription.bank.id,
        day: editableSubscription.day,
        limit: editableSubscription.amount,
        period: editableSubscription.period,
        mobile: response.mobile,
      },
      onSuccess: (response) => {
        onSubmit(response);
      },
    });
  };

  const onSubmit = (response) => {
    if (!process.env.REACT_APP_BASE_URL.includes("dev")) {
      sendEnableRecurring();
    }
    if (response.url) {
      window.open(response.url, "_self");
    } else {
      getSubscriptionStatus();
      openSnack(true, {
        message: "اشتراک پس‌انداز خودکار با موفقیت ثبت شد.",
      });
    }
  };

  return (
    <>
      <AppBarContainer
        rightElement={
          <IconButton
            onClick={() => {
              if (
                serverSubscription.status === DirectDebitStatusType.ACTIVE &&
                !isButtonDisable()
              ) {
                setOpenEditModal(true);
              } else {
                navigate(-1);
              }
            }}
          >
            <ArrowForward sx={{ color: "common.white" }} />
          </IconButton>
        }
        centerElement={
          <Typography variant="h5" color={"common.white"}>
            پس‌انداز خودکار
          </Typography>
        }
      />

      <PageContentContainer
        sx={{
          overflow: "auto",
          display: "block",
          pb: enableChecked ? "112px" : "24px",
        }}
      >
        {getLoading ? (
          <>
            <CustomTextSkeleton width="100%" />
            <CustomRectangularSkeleton sx={{ mt: 2 }} height={80} />
            <CustomTextSkeleton width={200} sx={{ mt: 4 }} />
            <CustomTextSkeleton width="100%" sx={{ mt: 2 }} />
            <CustomTextSkeleton width="100%" sx={{ mt: 2 }} />
          </>
        ) : (
          serverSubscription && (
            <>
              <Box
                sx={{
                  mb: "24px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="grey.2">
                  فعال سازی پس‌انداز خودکار
                </Typography>
                <Switch
                  checked={enableChecked}
                  onChange={(event) => {
                    if (
                      serverSubscription.status === DirectDebitStatusType.ACTIVE
                    ) {
                      if (!event.target.checked) {
                        setOpenModal(true);
                      } else {
                        setEnableChecked(event.target.checked);
                      }
                    } else {
                      setEnableChecked(event.target.checked);
                    }
                  }}
                />
              </Box>
              {enableChecked ? (
                <>
                  <Box
                    sx={{
                      width: "100%",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      pb: 2,
                    }}
                  >
                    <EditSubscriptionBox
                      editableSubscription={editableSubscription}
                      serverSubscription={serverSubscription}
                      errorMessage={errorMessage}
                    />
                  </Box>
                  <BottomFullWidthButtonContainer>
                    <LoadingButton
                      disabled={isButtonDisable()}
                      onClick={save}
                      loading={preLoading || loading}
                      sx={{
                        color: "common.white",
                        typography: "h3",
                      }}
                      variant="contained"
                      fullWidth
                    >
                      ذخیره
                    </LoadingButton>
                  </BottomFullWidthButtonContainer>
                </>
              ) : (
                <Box sx={{ width: "100%" }}>
                  <InfoBox
                    title="چرا پس‌انداز خودکار؟"
                    description={
                      <>
                        خیلی وقت‌ها فراموشی یا تنبلی باعث می‌شود تا فرصت
                        پس‌انداز کردن را از دست بدهیم. <br /> با «پس‌انداز
                        خودکار»، مبلغ دلخواه شما به صورت خودکار از طریق حساب
                        بانکی متصل به قلک بلوط پس‌انداز می‌شود
                      </>
                    }
                  />
                </Box>
              )}

              {mobileBoxVisibility && (
                <MobileNoBottomSheet
                  open={open}
                  setMobileNo={setMobileNo}
                  setOpen={setOpen}
                  submit={onSubmit}
                  mobileNo={mobileNo}
                />
              )}
            </>
          )
        )}
      </PageContentContainer>
      <ActionModal
        open={openModal}
        setOpen={setOpenModal}
        title="غیر فعال‌سازی"
        description={
          <>
            آیا از غیرفعال‌سازی{" "}
            <Box sx={{ display: "inline", typography: "h5", color: "grey.2" }}>
              پس‌انداز خودکار
            </Box>{" "}
            مطمئن هستید؟{" "}
          </>
        }
        isContained={false}
        actionTitle="غیر فعال‌سازی"
        action={onDelete}
        cancelTitle="بستن"
        loading={deleteLoading}
      />
      <ActionModal
        open={openEditModal}
        setOpen={setOpenEditModal}
        title="تغییرات ذخیره نشده"
        description="در این صفحه تغییراتی دارید که ذخیره نشده‌اند  آیا از ترک صفحه اطمینان دارید؟"
        isContained
        actionTitle="ماندن"
        action={() => {
          setOpenEditModal(false);
        }}
        cancelTitle="بله"
        cancelAction={() => navigate(-1)}
      />
    </>
  );
}

export default DirectDebitMainPage;
